import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Tooltip, Typography, Badge } from '@mui/material';
import { useRecoilState, useRecoilValue, useSetRecoilState, useRecoilCallback } from 'recoil';
import { useTranslation } from 'react-i18next';
import { atom_tourState } from '@app/shared/state/tour.atom';
import { atom_bookmarkedStopsState } from '@app/shared/state/filter.atom';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { Stop, Tour } from '@app/shared/models/tour.type';
import { sideBarStateAtom } from '@app/shared/state/sidebar.atom';
import { atom_listModeState, atom_mobileListHeight } from '@app/shared/state/list-mode.atom';
import { atom_flyToFitStops } from '@app/shared/state/map-ref.atom';
import type { SideBarState } from '@app/shared/state/sidebar.atom';
import { flyAfterLayout } from '@app/shared/utils/fly-after-layout';

// Tour main color
const TOUR_MAIN_COLOR = '#D0552E';

interface SavedButtonProps {
    onStateChange?: (state: SideBarState) => void;
    isDesktop?: boolean;
    mobileWidth?: string;
}

export function SavedButton({ onStateChange, isDesktop = true, mobileWidth = '20%' }: SavedButtonProps) {
    const { t } = useTranslation();
    const [activeState, setActiveState] = useRecoilState(sideBarStateAtom);
    const activeTour = useRecoilValue(atom_tourState);
    const bookmarkedStops = useRecoilValue(atom_bookmarkedStopsState);
    const setActiveStopState = useSetRecoilState(atom_activeStopState);
    const setListMode = useSetRecoilState(atom_listModeState);
    const [animateSaved, setAnimateSaved] = useState(false);
    const prevSavedCountRef = useRef(bookmarkedStops.length);

    const isActive = activeState === 'saved';
    const isSavedDisabled = bookmarkedStops.length === 0;

    // Get main color from tour config, fallback to default
    const mainColor = activeTour?.config?.mainColor || TOUR_MAIN_COLOR;

    // Get default list mode based on device and tour config
    const getDefaultListMode = () => {
        if (isDesktop) return 'desktop';
        if (activeTour?.config?.['show_list']) return 'mobileList';
        return 'mobileSwiper';
    };

    // Delegate saved-bounds fly to useMapNavigation's flyToFitStops (via atom).
    // That path handles single vs multi, supercluster-aware zoom, topBar + CTA/list
    // padding in one place — so behavior matches the rest of the app.
    const flyToSavedBounds = useRecoilCallback(({ snapshot }) => () => {
        console.log('[FIT-DEBUG] flyToSavedBounds INVOKED at', Date.now());
        const flyToFitStops = snapshot.getLoadable(atom_flyToFitStops).contents as
            | ((stops: Stop[], options?: { padding?: number; maxZoom?: number }) => void)
            | null;
        if (!flyToFitStops) return;
        const bookmarkedStopObjects = (activeTour?.stops || []).filter((stop: Stop) =>
            bookmarkedStops.includes(stop.id)
        );
        if (bookmarkedStopObjects.length === 0) return;
        flyToFitStops(bookmarkedStopObjects, { padding: 50, maxZoom: 15 });
    }, [activeTour, bookmarkedStops]);

    // Animation effect when saved count changes
    useEffect(() => {
        if (prevSavedCountRef.current !== bookmarkedStops.length) {
            setAnimateSaved(true);
            const timeout = setTimeout(() => {
                setAnimateSaved(false);
            }, 600);
            prevSavedCountRef.current = bookmarkedStops.length;
            return () => clearTimeout(timeout);
        }
    }, [bookmarkedStops.length]);

    // Auto-deactivate saved mode when bookmarked stops become empty
    useEffect(() => {
        if (bookmarkedStops.length === 0 && isActive) {
            setActiveState(null);
            setListMode(getDefaultListMode());
            onStateChange?.(null);
        }
    }, [bookmarkedStops.length, isActive, setActiveState, setListMode, onStateChange]);

    const handleClick = useRecoilCallback(({ set, snapshot }) => async () => {
        const currentSideBar = await snapshot.getPromise(sideBarStateAtom);
        const currentBookmarks = await snapshot.getPromise(atom_bookmarkedStopsState);

        // If already active, toggle off
        if (currentSideBar === 'saved') {
            set(sideBarStateAtom, null);
            set(atom_listModeState, getDefaultListMode() as any);
            onStateChange?.(null);
            return;
        }

        if (currentBookmarks.length === 0) return;

        const heightBefore = snapshot.getLoadable(atom_mobileListHeight).contents as number;
        console.log('[FIT-DEBUG] SavedButton CLICK — pre-setListMode mobileListHeight=', heightBefore, 'at', Date.now());

        set(sideBarStateAtom, 'saved');
        set(atom_listModeState, isDesktop ? 'desktop' : 'mobileList');

        onStateChange?.('saved');
        // Defer the fly so the freshly-mounted MobileListContent has time to
        // measure itself and update atom_mobileListHeight before flyToFitStops
        // reads it. Otherwise the bottom-obstruction read returns the previous
        // panel's (often much taller) height.
        console.log('[FIT-DEBUG] SavedButton SCHEDULED flyAfterLayout at', Date.now());
        flyAfterLayout(() => {
          console.log('[FIT-DEBUG] SavedButton flyAfterLayout CALLBACK FIRED at', Date.now());
          flyToSavedBounds();
        });
    }, [isDesktop, onStateChange, getDefaultListMode, flyToSavedBounds]);

    return (
        <Box sx={{ width: isDesktop ? '100%' : 'auto', flex: isDesktop ? 'none' : 1, minWidth: 0 }}>
            <Tooltip
                title={`${t('saved') || 'Saved'}${bookmarkedStops.length > 0 ? ` (${bookmarkedStops.length})` : ''}`}
                placement={isDesktop ? 'right' : 'top'}
                arrow
            >
                <span style={{ width: '100%', display: 'block' }}>
                    <IconButton
                        onClick={handleClick}
                        disabled={isSavedDisabled}
                        aria-label={t('saved') || 'Saved'}
                        sx={{
                            position: 'relative',
                            width: '100%',
                            height: isDesktop ? 72 : 56,
                            paddingTop: isDesktop ? '10px' : '6px',
                            paddingBottom: 0,
                            borderRadius: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: isActive || animateSaved ? mainColor : isSavedDisabled ? '#ccc' : '#666',
                            bgcolor: 'transparent',
                            transition: 'transform 0.3s ease',
                            transform: animateSaved ? 'scale(1.1)' : 'scale(1)',
                            '&:hover': {
                                bgcolor: 'transparent',
                            },
                            '&.Mui-disabled': {
                                color: '#ccc',
                            },
                            // Indicator: left on desktop, bottom on mobile
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                ...(isDesktop ? {
                                    left: -1,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '4px',
                                    height: '70%',
                                    borderRadius: '0 4px 4px 0',
                                } : {
                                    bottom: 0,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: '70%',
                                    height: '3px',
                                    borderRadius: '4px 4px 0 0',
                                }),
                                bgcolor: isActive || animateSaved ? mainColor : 'transparent',
                            },
                        }}
                    >
                        <Badge
                            badgeContent={bookmarkedStops.length > 0 ? bookmarkedStops.length : null}
                            color="error"
                            sx={{
                                '& .MuiBadge-badge': {
                                    fontSize: '10px',
                                    minWidth: '16px',
                                    height: '16px',
                                    top: -2,
                                    right: -2,
                                },
                            }}
                        >
                            <i
                                className={isActive || animateSaved ? "fa-solid fa-heart" : "fa-regular fa-heart"}
                                style={{
                                    fontSize: isDesktop ? '22px' : '20px',
                                }}
                                aria-hidden="true"
                            />
                        </Badge>
                        <Typography
                            variant="caption"
                            sx={{
                                mt: 0.5,
                                fontSize: isDesktop ? '0.75rem' : '0.6rem',
                                lineHeight: 1.1,
                                color: isSavedDisabled ? '#ccc' : 'inherit',
                            }}
                        >
                            {t('saved') || 'Saved'}
                        </Typography>
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
}
